import React, { useState, useEffect, useRef } from 'react';
import { Send, MoreVertical, Bot, Loader2, Sparkles, RefreshCcw, Trash2, ArrowDownCircle, Image as ImageIcon, X, Shield, FileText } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAIChat } from '../hooks/useAIChat';
import IAReportGenerator from './IAReportGenerator';

/**
 * AI DESK COPARMEX - V9.1.5-SHIELDED (Abril 04, 2026)
 * 
 * Motor ultra-estable con redundancia OpenRouter/Anthropic/Gemini.
 */

interface AIDeskProps {
  user: any;
}

const AIDeskCoparmex: React.FC<AIDeskProps> = ({ user }) => {
  const [input, setInput] = useState('');
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showReportGenerator, setShowReportGenerator] = useState(false);
  
  const { 
    messages, 
    sendMessage, 
    isLoading, 
    clearChat, 
    remainingQueries
  } = useAIChat(user);

  const handleScroll = () => {
    if (!chatContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
    setShowScrollDown(!isAtBottom);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  const handleSendMessage = async () => {
    if ((!input.trim() && selectedImages.length === 0) || isLoading) return;

    const userMsg = input.trim();
    const imagesToUpload = [...selectedImages];
    
    setInput('');
    setSelectedImages([]);
    
    await sendMessage(userMsg, imagesToUpload);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImages(prev => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file as any);
    });
  };

  const removeImage = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="flex flex-col h-full bg-[#0a0f1e] text-slate-100 font-sans tracking-tight relative overflow-hidden rounded-[2.5rem] border border-white/5">
      
      {/* GLOW DECORATIONS - REMOVED TOP LEFT TO FIX DOUBLE IMAGE PERCEPTION */}
      <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-blue-500/5 rounded-full blur-[120px] pointer-events-none" />

      {/* HEADER PREMIUM */}
      <header className="flex-shrink-0 flex items-center justify-between px-6 py-4 bg-black/40 backdrop-blur-2xl border-b border-white/5 z-20 shadow-2xl">
        <div className="flex items-center gap-4">
          <motion.div 
            className="flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-[#00C853] to-[#00E676]"
          >
            <Bot className="w-7 h-7 text-black stroke-[2.5px]" />
          </motion.div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-black tracking-tighter">
                AI <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-emerald-200">DESK</span>
              </h1>
              <span className="bg-[#3D5AFE] text-white px-2 py-0.5 rounded text-[10px] uppercase font-black tracking-widest">
                V9.1.5-SHIELDED
              </span>
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00C853] animate-pulse" />
              <p className="text-[10px] text-white/40 font-bold uppercase tracking-widest opacity-80">
                CONECTADO A OPENROUTER DIRECT ENGINE — ACCESO ILIMITADO
              </p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          {/* COUNTERS */}
          <div className="hidden md:flex flex-col items-end gap-1">
             <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-[#00C853] animate-pulse">
                <span>Motor:</span>
                <span>ONLINE / ILIMITADO</span>
             </div>
             <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-white/30">
                <span>Estado:</span>
                <span>OPENROUTER DIRECT 1.0</span>
             </div>
          </div>

          <div className="w-px h-8 bg-white/10 mx-2" />

          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowReportGenerator(true)}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 rounded-xl border border-emerald-500/20 transition-all group"
          >
            <FileText className="w-4 h-4 group-hover:rotate-6 transition-transform" />
            <span className="text-[10px] font-black uppercase tracking-widest hidden sm:block">Reportes IA</span>
          </motion.button>

          <div className="w-px h-8 bg-white/10 mx-2" />

          <button 
            onClick={() => clearChat()}
            title="Reiniciar Sesión"
            className="p-3 rounded-xl hover:bg-red-500/10 transition-all text-white/30 hover:text-red-400 group border border-transparent hover:border-red-500/20"
          >
            <Trash2 className="w-5 h-5 group-active:scale-90" />
          </button>
        </div>
      </header>

      {/* REPORT GENERATOR OVERLAY */}
      <AnimatePresence>
        {showReportGenerator && (
          <motion.div 
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="absolute inset-0 z-[100] bg-[#0a0f1e]"
          >
            <IAReportGenerator 
              user={user} 
              onClose={() => setShowReportGenerator(false)} 
            />
          </motion.div>
        )}
      </AnimatePresence>


      {/* MESSAGES AREA - AÑADIDO PT-24 PARA EVITAR SOLAPE CON HEADER */}
      <div 
        ref={chatContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-6 pt-24 pb-8 space-y-10 scroll-smooth custom-scrollbar relative"
      >
        {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full opacity-10 pointer-events-none">
                <Shield className="w-16 h-16 mb-4 stroke-[1] text-[#00E5FF]" />
            </div>
        )}

        <AnimatePresence mode="popLayout">
          {messages.map((msg, idx) => (
            <motion.div 
              key={`${msg.role}-${idx}`}
              initial={{ opacity: 0, y: 30, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 25 }}
              className={`flex items-start gap-6 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
            >
              {/* AVATAR */}
              <div className={`flex-shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center font-black text-[12px] shadow-2xl relative
                ${msg.role === 'assistant' 
                  ? 'bg-gradient-to-br from-[#00C853] to-[#009624] text-black ring-4 ring-[#00C853]/10' 
                  : 'bg-gradient-to-br from-[#3D5AFE] to-[#2979FF] text-white ring-4 ring-[#3D5AFE]/10'}`}
              >
                {msg.role === 'assistant' ? <Bot className="w-6 h-6" /> : 'USER'}
              </div>

              {/* BUBBLE */}
              <div className={`relative max-w-[80%] group`}>
                <div className={`p-6 rounded-[2rem] text-[15.5px] leading-[1.7] shadow-2xl backdrop-blur-3xl border transition-all duration-300
                  ${msg.role === 'assistant' 
                    ? 'bg-slate-900/40 border-slate-800 text-slate-100 rounded-tl-none hover:bg-slate-900/60' 
                    : 'bg-gradient-to-br from-[#3D5AFE]/20 to-[#2979FF]/10 border-[#3D5AFE]/30 text-white rounded-tr-none hover:border-[#3D5AFE]/50'}`}
                >
                  {/* IMAGES IN MESSAGE */}
                  {msg.images && msg.images.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                        {msg.images.map((img, i) => (
                            <img key={i} src={img} alt="Shared content" className="w-32 h-32 object-cover rounded-xl border border-white/10 shadow-lg" />
                        ))}
                    </div>
                  )}

                  <div className="whitespace-pre-wrap">
                    {msg.content}
                  </div>
                  
                  {/* METADATA PILL */}
                  <div className={`absolute bottom-[-24px] ${msg.role === 'user' ? 'right-4' : 'left-4'} flex items-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity`}>
                    <span className="text-[9px] font-black text-white/20 uppercase tracking-[2px]">
                      {msg.timestamp || 'Real-time Analysis'}
                    </span>
                    {msg.engine && (
                        <span className="text-[9px] font-black text-[#00C853]/40 uppercase tracking-[2px]">
                            via {msg.engine}
                        </span>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* LOADING ANIMATION */}
        {isLoading && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex items-start gap-6"
          >
            <div className="w-12 h-12 rounded-2xl bg-[#00C853] flex items-center justify-center font-black text-black animate-pulse shadow-lg shadow-green-500/30">
              AI
            </div>
            <div className="bg-slate-900/40 border border-slate-800 p-8 rounded-[2rem] rounded-tl-none shadow-2xl backdrop-blur-3xl">
              <div className="flex gap-3">
                {[0, 1, 2].map((i) => (
                  <motion.div 
                    key={i}
                    animate={{ y: [0, -8, 0], scale: [1, 1.2, 1] }}
                    transition={{ repeat: Infinity, duration: 1.2, delay: i * 0.2, ease: "easeInOut" }}
                    className="w-3 h-3 bg-[#00C853] rounded-full shadow-[0_0_15px_rgba(0,200,83,0.6)]"
                  />
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* SCROLL DOWN */}
        <AnimatePresence>
            {showScrollDown && (
            <motion.button 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                onClick={scrollToBottom}
                className="fixed bottom-36 right-10 p-4 bg-[#00C853] border border-[#00C853]/20 rounded-full shadow-[0_10px_40px_rgba(0,200,83,0.3)] text-black transition-all z-30 hover:scale-110 active:scale-95"
            >
                <ArrowDownCircle className="w-6 h-6 stroke-[3px]" />
            </motion.button>
            )}
        </AnimatePresence>

        <div ref={messagesEndRef} className="h-6" />
      </div>

      {/* INPUT AREA */}
      <footer className="p-8 bg-[#030712] border-t border-white/5 relative z-20 shadow-[0_-30px_60px_rgba(0,0,0,0.6)]">
        
        {/* IMAGE PREVIEW AREA */}
        <AnimatePresence>
            {selectedImages.length > 0 && (
                <motion.div 
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="max-w-4xl mx-auto flex gap-4 overflow-x-auto pb-6 custom-scrollbar"
                >
                    {selectedImages.map((img, i) => (
                        <div key={i} className="relative flex-shrink-0 group">
                            <img src={img} alt="Upload preview" className="w-24 h-24 object-cover rounded-2xl border-2 border-[#00C853]/30 shadow-xl" />
                            <button 
                                onClick={() => removeImage(i)}
                                className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </div>
                    ))}
                    <button 
                        onClick={() => fileInputRef.current?.click()}
                        className="w-24 h-24 rounded-2xl border-2 border-dashed border-white/10 flex items-center justify-center text-white/20 hover:border-[#00C853]/40 hover:text-[#00C853]/40 transition-all flex-shrink-0"
                    >
                        <ImageIcon className="w-8 h-8" />
                    </button>
                </motion.div>
            )}
        </AnimatePresence>

        <div className="max-w-4xl mx-auto flex items-end gap-4">
          <button 
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading}
            className={`flex-shrink-0 w-[60px] h-[60px] rounded-2xl border border-white/10 flex items-center justify-center transition-all
                ${isLoading ? 'opacity-20 translate-y-1' : 'hover:bg-white/5 hover:border-[#00C853]/20 hover:text-[#00C853] active:scale-95'}`}
          >
            <ImageIcon className="w-6 h-6" />
          </button>
          <input 
            type="file" 
            ref={fileInputRef} 
            onChange={handleImageUpload} 
            multiple 
            accept="image/*" 
            className="hidden" 
          />

          <div className="flex-1 relative group">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Escribe tu consulta o desafío estratégico..."
              rows={1}
              className="w-full bg-white/5 border border-white/10 rounded-2xl py-5 pl-8 pr-16 text-[16px] focus:outline-none focus:border-[#00C853]/50 focus:ring-4 focus:ring-[#00C853]/10 transition-all resize-none placeholder:text-white/10 group-hover:bg-white/[0.08] custom-scrollbar"
              style={{ minHeight: '60px', maxHeight: '180px' }}
            />
            <div className="absolute right-6 bottom-4.5">
              <span className="text-[10px] text-white/5 font-black hidden md:block uppercase tracking-widest">Shift + Enter</span>
            </div>
          </div>
          
          <button
            onClick={handleSendMessage}
            disabled={(!input.trim() && selectedImages.length === 0) || isLoading}
            className={`flex-shrink-0 w-[60px] h-[60px] rounded-2xl flex items-center justify-center transition-all duration-300
              ${(input.trim() || selectedImages.length > 0) && !isLoading 
                ? 'bg-[#00C853] text-black shadow-2xl shadow-green-500/40 hover:scale-105 active:scale-95 group' 
                : 'bg-white/5 text-white/5 cursor-not-allowed'}`}
          >
            {isLoading ? (
              <Loader2 className="w-8 h-8 animate-spin" />
            ) : (
              <Send className="w-7 h-7 stroke-[2.5px] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            )}
          </button>
        </div>
        
        <div className="flex items-center justify-center gap-10 mt-6 text-[10px] text-white/5 font-black uppercase tracking-[5px]">
          <span className="flex items-center gap-2 italic">
            <Bot className="w-3 h-3" />
            Gemini 2.5 Flash-Lite
          </span>
          <span className="w-1 h-1 rounded-full bg-white/5" />
          <span>Multimodal Ops</span>
          <span className="w-1 h-1 rounded-full bg-white/5" />
          <span>Secured by CPX-SHIELD</span>
        </div>
      </footer>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.04);
          border-radius: 40px;
          border: 2px solid transparent;
          background-clip: content-box;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.1);
          border: 2px solid transparent;
          background-clip: content-box;
        }
      `}</style>
    </div>
  );
};

export default AIDeskCoparmex;
